import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { fmtDate, ReasonDialog, type PlatformCall } from './common';

interface Operator {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  has_password: boolean;
  last_login_at: string | null;
  created_at: string | null;
}

type Pending = { op: Operator; action: 'disable' | 'enable' | 'reset-password' } | null;

/**
 * The console's own accounts. Nobody can disable themselves or the last
 * active operator — the API refuses both, so the console can never be left
 * with no one able to sign in to it.
 */
export function OperatorsTab({ call, meId }: { call: PlatformCall; meId: string | null }) {
  const [ops, setOps] = useState<Operator[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ email: string; password: string } | null>(null);
  const [pending, setPending] = useState<Pending>(null);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);

  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [pwNote, setPwNote] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setOps(((await call('/admins')) as { admins: Operator[] }).admins);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [call]);

  useEffect(() => { void load(); }, [load]);

  const add = async () => {
    setAdding(true);
    setErr(null);
    try {
      const body = (await call('/admins', {
        method: 'POST', body: JSON.stringify({ email: email.trim(), full_name: name.trim() || null }),
      })) as Operator & { password?: string };
      if (body.password) setSecret({ email: body.email, password: body.password });
      setEmail('');
      setName('');
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setAdding(false);
    }
  };

  const changeOwn = async () => {
    setPwNote(null);
    try {
      await call('/me/password', {
        method: 'POST', body: JSON.stringify({ current_password: cur, new_password: next }),
      });
      setCur('');
      setNext('');
      setPwNote({ ok: true, text: 'Password changed.' });
    } catch (e) {
      setPwNote({ ok: false, text: (e as Error).message });
    }
  };

  if (err && !ops) return <Alert severity="error">{err}</Alert>;
  if (!ops) return <CircularProgress size={24} />;

  const titles = {
    disable: 'Disable this operator?',
    enable: 'Re-enable this operator?',
    'reset-password': 'Reset this operator’s password?',
  } as const;

  return (
    <Stack spacing={2}>
      {err && <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>}
      {secret && (
        <Alert severity="success" onClose={() => setSecret(null)}>
          Password for <strong>{secret.email}</strong>:{' '}
          <code style={{ userSelect: 'all' }}>{secret.password}</code> — shown once. Hand it over
          securely and ask them to change it after signing in.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Operator</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last sign-in</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {ops.map((o) => (
              <TableRow key={o.id} hover>
                <TableCell>
                  <Typography variant="body2">{o.full_name ?? o.email}{o.id === meId ? ' (you)' : ''}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.email}</Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={o.is_active ? 'Active' : 'Disabled'}
                    color={o.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell><Typography variant="caption">{fmtDate(o.last_login_at)}</Typography></TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {o.id !== meId && (
                    <>
                      <Button size="small" onClick={() => setPending({ op: o, action: 'reset-password' })}>
                        Reset password
                      </Button>
                      {o.is_active ? (
                        <Button size="small" color="error" onClick={() => setPending({ op: o, action: 'disable' })}>
                          Disable
                        </Button>
                      ) : (
                        <Button size="small" onClick={() => setPending({ op: o, action: 'enable' })}>Enable</Button>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Add an operator</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Operators can see every workspace, approve signups and disable any account.
            A password is generated and shown to you once.
          </Typography>
          <Stack spacing={1.5}>
            <TextField size="small" label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField size="small" label="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
            <Box>
              <Button variant="contained" disabled={adding || !email.includes('@')} onClick={() => void add()}>
                {adding ? 'Adding…' : 'Add operator'}
              </Button>
            </Box>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Change your password</Typography>
          <Stack spacing={1.5}>
            <TextField size="small" type="password" label="Current password" value={cur}
              onChange={(e) => setCur(e.target.value)} autoComplete="current-password" />
            <TextField size="small" type="password" label="New password (12+ characters)" value={next}
              onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
            {pwNote && <Alert severity={pwNote.ok ? 'success' : 'error'}>{pwNote.text}</Alert>}
            <Box>
              <Button variant="outlined" disabled={!cur || next.length < 12} onClick={() => void changeOwn()}>
                Change password
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Stack>

      <ReasonDialog
        open={!!pending}
        title={pending ? titles[pending.action] : ''}
        description={pending?.action === 'disable'
          ? `${pending.op.email} is signed out on their next request and cannot sign in again until re-enabled.`
          : pending?.action === 'reset-password'
            ? `A new password is generated and shown to you once. ${pending.op.email}'s old password stops working.`
            : pending?.op.email}
        confirmLabel={pending?.action === 'disable' ? 'Disable' : pending?.action === 'enable' ? 'Enable' : 'Reset'}
        danger={pending?.action === 'disable'}
        onClose={() => setPending(null)}
        onConfirm={async (reason) => {
          if (!pending) return;
          const body = (await call(`/admins/${pending.op.id}/${pending.action}`, {
            method: 'POST', body: JSON.stringify({ reason }),
          })) as { email: string; password?: string };
          if (body.password) setSecret({ email: body.email, password: body.password });
          await load();
        }}
      />
    </Stack>
  );
}
